import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';
import { equals } from 'remeda';

import { Box, Button, Typography } from '@myn/mui';

import { BUILDER_ROW_TYPES } from '../../../../constants/campaignBuilder.js';
import { useGetMediaPlanRows } from '../../../../hooks/useGetMediaPlanRows.js';
import { useIsArchivedVersion } from '../../../../hooks/useIsArchivedVersion.js';
import { useSelectedBuyingPlanId } from '../../../../hooks/useSelectedBuyingPlanId.js';
import { MediaPlanRow } from '../../../../types/campaignBuilder.js';
import { Platform } from '../../../../types/integrations.js';
import { useAddMediaPlanRow } from '../../../../utils/campaigns/myCampaigns.js';
import { ChannelRowType } from '../../types.js';
import { DragItemRow } from '../DragItemRow.jsx';
import EmptyItem from '../EmptyItem.jsx';

type Props = {
  channel: ChannelRowType;
  isSearchPerformed: boolean;
  toggleRowExpanded: (id: number, isOpen: boolean) => void;
  setRowState: (id: number[], { isNewAdded }: { isNewAdded: boolean }) => void;
};

export const ChannelBlock = memo(
  ({ channel, isSearchPerformed, toggleRowExpanded, setRowState }: Props) => {
    const { t } = useTranslation('@myn/planning');

    const selectedPlanId = useSelectedBuyingPlanId();

    const preparedMediaPlanRows = useGetMediaPlanRows();
    const [isArchivedVersion] = useIsArchivedVersion();
    const [channelToAdd, setChannelToAdd] = useState<string | null>(null);

    const { handleAddChannelRequest, handleAddPlatformRequest } =
      useAddMediaPlanRow();

    const [showAll, setShowAll] = useState<boolean>(false);
    const [platformToAdd, setPlatformToAdd] = useState<Platform[]>([]);
    const [rowToAdd, setRowToAdd] = useState<Platform[]>([]);
    const channelPlatforms = channel.platforms;
    const slicePlatforms = (channelPlatforms?.length || 0) > 4;
    const isNoMorePlatformsToShow = (channelPlatforms || []).length <= 4;

    const selectedChannels: {
      [name: string]: { name: string; type: string }[];
    } = useMemo(
      () =>
        preparedMediaPlanRows.reduce(
          (acc: MediaPlanRow[], { name, subRows }: MediaPlanRow) => ({
            ...acc,
            [name || '']: subRows?.map((row) => ({
              name: row.name,
              type: row.type,
            })),
          }),
          {},
        ),
      [preparedMediaPlanRows],
    );

    const platforms = useMemo(() => {
      if (!slicePlatforms || showAll) {
        return channelPlatforms;
      }
      return channelPlatforms?.slice(0, 4);
    }, [channel, slicePlatforms, showAll]);

    const handleToggleShowAll = () => setShowAll(!showAll);

    const checkInverted = useCallback(
      (channelName: string, platformName: string) => {
        const platformExists = selectedChannels[channelName]?.find(
          ({ name }) => name === platformName,
        );
        const platformToAddExists = platformToAdd.find(
          ({ platform }) => platform === platformName,
        );
        return !!platformExists || !!platformToAddExists;
      },
      [selectedChannels, rowToAdd],
    );
    const isChannelAdded = !!selectedChannels[channel.name];

    const handleAddChannel = useCallback(() => {
      if (isArchivedVersion || isChannelAdded) {
        return;
      }
      setChannelToAdd(channel.name);
      handleAddChannelRequest(
        {
          type: BUILDER_ROW_TYPES.CHANNEL,
          parent: null,
          mediaPlan: selectedPlanId,
          channel,
        },
        setRowState,
      );
    }, [channel, isArchivedVersion, isChannelAdded]);

    const handleAddPlatform = useCallback(
      (platform: Platform) => () => {
        if (isArchivedVersion) {
          return;
        }

        if (!isChannelAdded) {
          handleAddChannel();
        }
        setPlatformToAdd((prevState) => [...prevState, platform]);
        setRowToAdd((prevState) => [...prevState, platform]);
      },
      [channel, isChannelAdded, isArchivedVersion],
    );

    useEffect(() => {
      if (isChannelAdded && platformToAdd.length) {
        const currentChannel = preparedMediaPlanRows.find(
          (mediaRow: MediaPlanRow) => mediaRow.name === channel.name,
        );

        platformToAdd.forEach(async ({ typeForAdd, platform, label }) => {
          const newRow = {
            label,
            type: typeForAdd,
            name: platform,
            channel: currentChannel,
            currentChannelId: currentChannel?.id,
            mediaPlan: selectedPlanId,
          };
          //@ts-ignore
          handleAddPlatformRequest(newRow, toggleRowExpanded, setRowState);
        });

        setPlatformToAdd([]);
      }
    }, [isChannelAdded, platformToAdd]);

    useEffect(() => {
      if (isChannelAdded && channelToAdd) {
        setChannelToAdd(null);
      }
    }, [selectedChannels]);

    useEffect(() => {
      if (isChannelAdded && rowToAdd.length) {
        const newRows = rowToAdd.filter(
          (platform) =>
            !selectedChannels[channel.name]?.find(
              ({ name }) => name === platform.platform,
            ),
        );
        if (!equals(newRows, rowToAdd)) {
          setRowToAdd(newRows);
        }
      }
    }, [selectedChannels, rowToAdd]);

    return (
      <Box>
        <Label variant="overline01">{channel.label}</Label>
        {platforms?.length ? (
          <Container>
            {!isSearchPerformed && (
              <DragItemRow
                row={{ ...channel, rowType: BUILDER_ROW_TYPES.CHANNEL }}
                inverted={isChannelAdded || !!channelToAdd || isArchivedVersion}
                isChannelRow
                onDoubleClick={handleAddChannel}
              />
            )}
            {platforms.map((platform) => (
              <DragItemRow
                key={`${platform.platform}${platform.type}`}
                row={{
                  ...platform,
                  channelName: channel.name,
                  channelLabel: channel.label,
                  rowType: platform.typeForAdd,
                }}
                inverted={
                  checkInverted(channel.name, platform.platform) ||
                  isArchivedVersion
                }
                onDoubleClick={handleAddPlatform(platform)}
              />
            ))}
            {slicePlatforms && !isNoMorePlatformsToShow && (
              <Button
                aria-label={showAll ? 'show less options' : 'show more options'}
                color="primary"
                variant="text"
                size="medium"
                onClick={handleToggleShowAll}
              >
                {showAll ? t('Hide') : t('Show more')}
              </Button>
            )}
          </Container>
        ) : (
          <EmptyItem />
        )}
      </Box>
    );
  },
);

const Label = styled(Typography)`
  display: block;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme: themeV2 }) => themeV2.palette.text.secondary};
`;

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;
